<template>
  <div 
    ref="imageContainer"
    class="message-card-image relative rounded-lg overflow-hidden bg-white border border-gray-200"
    :style="containerStyle"
  >
    <!-- Placeholder while loading -->
    <div
      v-if="!imageLoaded && !imageError"
      class="flex items-center justify-center bg-gray-200 animate-pulse p-12 min-h-[200px]"
    >
      <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>

    <!-- Actual image (lazy loaded from blob) -->
    <img
      v-if="imageBlobUrl && !imageError"
      :src="imageBlobUrl"
      :alt="imageName"
      class="w-full h-auto object-cover cursor-pointer transition-opacity duration-300"
      :class="{ 'opacity-0': !imageLoaded, 'opacity-100': imageLoaded }"
      @load="handleImageLoad"
      @error="handleImageError"
      @click="openImagePreview"
    />

    <!-- Error state -->
    <div
      v-if="imageError"
      class="flex items-center justify-center bg-red-50 p-12 min-h-[200px]"
    >
      <div class="text-center">
        <svg class="w-12 h-12 text-red-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="text-sm text-red-600 font-medium">Failed to load image</p>
      </div>
    </div>

    <!-- Image info overlay (on hover) -->
    <div
      v-if="imageLoaded && !imageError"
      class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 hover:opacity-100 transition-opacity"
    >
      <p class="text-white text-xs truncate">{{ imageName }}</p>
      <p class="text-white/80 text-xs">{{ formatFileSize(imageSize) }}</p>
    </div>

    <!-- Image preview modal (teleported to body to avoid z-index stacking context issues) -->
    <teleport to="body">
      <div
        v-if="showPreview"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75"
        @click="closeImagePreview"
      >
        <button
          class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          @click="closeImagePreview"
        >
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <img
          v-if="imageBlobUrl"
          :src="imageBlobUrl"
          :alt="imageName"
          class="max-w-[90vw] max-h-[90vh] object-contain"
          @click.stop
        />
      </div>
    </teleport>
  </div>
</template>

<script>
import { createVisibilityObserver, disconnectObserver } from '@/utils/viewportObserver';
import transServiceV2 from '@/services/transServiceV2';

export default {
  name: 'MessageCardImage',
  props: {
    attachment: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      imageId: '',
      imageName: '',
      imageSize: 0,
      imageType: '',
      imageBlobUrl: null,
      imageLoaded: false,
      imageError: false,
      observer: null,
      showPreview: false,
      imageDimensions: null // { width, height }
    };
  },
  computed: {
    containerStyle() {
      if (!this.imageDimensions) {
        return { maxWidth: '600px' };
      }
      
      const maxWidth = 600; // Maximum width in pixels
      const { width, height } = this.imageDimensions;
      
      // If image width is less than max, use actual width
      if (width <= maxWidth) {
        return {
          width: `${width}px`,
          maxWidth: `${maxWidth}px`
        };
      }
      
      // If image width exceeds max, scale down proportionally
      const scaledHeight = (height * maxWidth) / width;
      return {
        width: `${maxWidth}px`,
        maxWidth: `${maxWidth}px`
      };
    }
  },
  mounted() {
    this.parseImageData();
    this.setupLazyLoading();
  },
  beforeUnmount() {
    this.cleanupObserver();
    this.revokeBlobUrl();
  },
  methods: {
    parseImageData() {
      // Extract image data from attachment fields
      const fields = this.attachment.fields || [];
      const getField = (title) => fields.find(f => f.title === title)?.value || '';
      
      this.imageId = getField('id');
      this.imageName = getField('name');
      this.imageSize = parseInt(getField('size')) || 0;
      this.imageType = getField('ftype');
    },

    setupLazyLoading() {
      // Create intersection observer for lazy loading
      this.observer = createVisibilityObserver((element) => {
        // When component enters viewport, load the image
        this.loadImage();
        // Unobserve after triggering load
        this.observer.unobserve(element);
      }, {
        rootMargin: '100px', // Start loading 100px before entering viewport
        threshold: 0.01
      });

      // Observe the image container
      this.$nextTick(() => {
        if (this.$refs.imageContainer) {
          this.observer.observe(this.$refs.imageContainer);
        }
      });
    },

    async loadImage() {
      if (!this.imageId || this.imageBlobUrl) {
        return;
      }

      try {
        console.log('🖼️ Loading image:', this.imageId);
        
        // Call decrypt API to get image blob
        const response = await transServiceV2({
          url: `/api/v1/patient-chat/images/decrypt/${this.imageId}`,
          method: 'get',
          responseType: 'blob'
        });

        // Create blob from response
        const blob = new Blob([response.data], { 
          type: response.headers['content-type'] || `image/${this.imageType.toLowerCase()}`
        });
        
        // Get actual image dimensions using createImageBitmap
        const bitmap = await createImageBitmap(blob);
        this.imageDimensions = {
          width: bitmap.width,
          height: bitmap.height
        };
        bitmap.close(); // Free memory
        
        // Create blob URL for display
        this.imageBlobUrl = URL.createObjectURL(blob);
        
        console.log('✅ Image loaded successfully:', this.imageId, this.imageDimensions);
      } catch (error) {
        console.error('❌ Failed to load image:', error);
        this.imageError = true;
      }
    },

    cleanupObserver() {
      disconnectObserver(this.observer);
    },

    revokeBlobUrl() {
      // Clean up blob URL to prevent memory leaks
      if (this.imageBlobUrl) {
        URL.revokeObjectURL(this.imageBlobUrl);
        this.imageBlobUrl = null;
      }
    },

    handleImageLoad() {
      this.imageLoaded = true;
    },

    handleImageError() {
      this.imageError = true;
      this.imageLoaded = false;
    },

    openImagePreview() {
      this.showPreview = true;
    },

    closeImagePreview() {
      this.showPreview = false;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
/* Smooth transitions */
img {
  transition: opacity 0.3s ease-in-out;
}

/* Hover effect for image containers */
.group:hover {
  transform: scale(1.02);
  transition: transform 0.2s ease-in-out;
}
</style>
